/**
 * MetricTreemap — Mini-treemap de las tarjetas KPI.
 *
 * QUÉ HACE: reparte el ancho del cuerpo en bloques proporcionales al valor de
 *   cada porción (treemap horizontal) con su color, etiqueta, monto y
 *   porcentaje. Los bloques crecen desde 0 al montar y migran al cambiar los
 *   valores. La usa la prop `treemap` de MetricCard (composición de la cartera).
 * QUÉ INVOCA: `useAnimatedSeries` (ancho animado de los bloques).
 * QUÉ EXPORTA: MetricTreemap, MetricTreemapProps.
 * QUÉ NO DEBE HACER: no calcula los datos ni accede a almacenamiento.
 */

import { useAnimatedSeries } from "@/hooks/useAnimatedSeries";

/** Bloque del treemap. */
interface MetricTreemapSlice {
  label: string;
  value: number;
  /** Color del bloque (hex, p. ej. "#10b981"). */
  color: string;
}

export interface MetricTreemapProps {
  slices: MetricTreemapSlice[];
  /** Formatea el valor de cada bloque (por defecto, crudo). */
  valueFormatter?: (value: number) => string;
  className?: string;
}

export const MetricTreemap = ({
  slices,
  valueFormatter = (value) => `${value}`,
  className = "",
}: MetricTreemapProps) => {
  const animated = useAnimatedSeries(slices, {
    keys: ["value"],
    stagger: 90,
  });
  const targetTotal = slices.reduce(
    (sum, slice) => sum + Math.max(0, slice.value),
    0,
  );
  const safeTotal = targetTotal > 0 ? targetTotal : 1;

  return (
    <div className={`flex min-h-0 flex-1 gap-1.5 ${className}`}>
      {animated.map((slice) => {
        const pct = (Math.max(0, slice.value) / safeTotal) * 100;
        return (
          <div
            key={slice.label}
            className="flex min-w-[2.25rem] flex-col justify-end overflow-hidden rounded-lg p-2"
            style={{
              flexGrow: Math.max(slice.value, 0.0001),
              flexBasis: 0,
              backgroundColor: `${slice.color}1a`,
              boxShadow: `inset 3px 0 0 0 ${slice.color}`,
            }}
          >
            <span
              className="truncate text-[9px] font-semibold leading-tight"
              style={{ color: slice.color }}
            >
              {slice.label}
            </span>
            <span className="truncate text-xs font-extrabold tabular-nums text-slate-800 dark:text-slate-100">
              {valueFormatter(slice.value)}
            </span>
            <span className="text-[9px] font-medium tabular-nums text-slate-400 dark:text-slate-500">
              {Math.round(pct)}%
            </span>
          </div>
        );
      })}
    </div>
  );
};
